import type { PayoutBucketKey } from '@/api/payees';

/**
 * 收款人列表「打款情况」列的展示定义。唯一真相源，别在组件里各写一份 switch。
 *
 * 桶的口径在后端（payees.service.ts 的 attachPayoutSummary），这里只管怎么显示。
 */
export const PAYOUT_BUCKET_LABEL: Record<PayoutBucketKey, string> = {
  PENDING: '待打款',
  IN_FLIGHT: '进行中',
  UNKNOWN: '状态未知',
  PAID: '已到账',
  FAILED: '失败',
};

/** 与 domain/payouts.ts 的 PAYOUT_STATUS_COLOR 保持一致，同一个状态不该有两种颜色。 */
export const PAYOUT_BUCKET_COLOR: Record<PayoutBucketKey, string> = {
  PENDING: 'gold',
  IN_FLIGHT: 'blue',
  UNKNOWN: 'volcano',
  PAID: 'green',
  FAILED: 'red',
};

/**
 * 🔴 每个桶的解释。这一列紧挨着收款人，最容易诱导「这个人好像没打成，再打一次」——
 * 而重复打款是本系统最严重的事故（PRODUCT.md §5.2）。文案必须挡住这个动作。
 */
export const PAYOUT_BUCKET_HINT: Record<PayoutBucketKey, string> = {
  PENDING: '已提交打款申请，财务还没执行',
  IN_FLIGHT: '已发起，结果待确认。这笔钱可能已经在路上，请勿据此重复打款',
  UNKNOWN: '查不到确定结果，需人工去空中云汇后台核对。绝不能当成失败重打',
  PAID: '已确认到账',
  FAILED: '已确认失败，钱没有打出去',
};

/**
 * 给金额加千分位。
 *
 * 🔴 **纯字符串操作，不经过 Number。** `Number(x).toLocaleString()` 看着更省事，
 * 但那一步就已经把 1234567.89 推进双精度了 —— 在这个仓里金额从不进 JS number
 * （PRODUCT.md §5.4）。后端给的已经是裁过尾零的十进制字符串，这里只插逗号。
 */
export function groupDigits(amount: string): string {
  const [int, frac] = amount.split('.');
  const grouped = (int ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac === undefined ? grouped : `${grouped}.${frac}`;
}
